import { useEffect, useState } from "react";
import { api } from "../api";
import ConfigurableDrawer from "./ConfigurableDrawer";
import { Badge, Btn, dt, eur } from "./common";
import { toast } from "sonner";
import { FileText, PenLine, Copy, ExternalLink, CheckCircle2, Link2, History, RotateCcw, Trash2 } from "lucide-react";
import { overlayCloseHandlers } from "../lib/overlayClose";

const FIELD_DEFS = [
  { key: "numero", label: "Numero", render: (p) => <span className="font-mono text-navy">{p.numero || "-"}</span> },
  { key: "titulo", label: "Titulo", render: (p) => p.titulo || "-" },
  { key: "account_nome", label: "Cliente", render: (p) => p.account_nome || "-" },
  { key: "estado", label: "Estado", render: (p) => <Badge value={p.estado} /> },
  { key: "total", label: "Total", render: (p) => eur(p.total || 0) },
  { key: "subtotal", label: "Subtotal", render: (p) => eur(p.subtotal || 0) },
  { key: "desconto_global", label: "Desconto global", render: (p) => p.desconto_pct_global > 0 ? `${p.desconto_pct_global}% (${eur(p.desconto_global_valor || 0)})` : "-" },
  { key: "iva", label: "IVA", render: (p) => eur(p.total_iva ?? p.iva ?? 0) },
  { key: "mrr", label: "MRR estimado", render: (p) => p.mrr_estimado > 0 ? eur(p.mrr_estimado) : "-" },
  { key: "condicoes", label: "Condicoes", render: (p) => `${p.condicoes_pagamento || "-"} / ${p.metodo_pagamento || "-"}` },
  { key: "expira_em", label: "Validade", render: (p) => p.expira_em ? dt(p.expira_em) : "-" },
  { key: "criado_em", label: "Criado", render: (p) => p.criado_em ? dt(p.criado_em) : "-" },
  { key: "notas", label: "Notas", render: (p) => p.notas || "-" },
];

const DEFAULT_ACTIONS = ["pdf", "copiar_numero", "assinatura"];
const DEFAULT_ACTIONS_VISIBLE = { pdf: true, copiar_numero: true, assinatura: true };

function makeActionDefs(onOpenPdf, onOpenSign) {
  return [
    { key: "pdf", label: "Ver PDF", icon: FileText, enabled: (p) => !!p.id,
      run: (p) => onOpenPdf && onOpenPdf(p) },
    { key: "copiar_numero", label: "Copiar numero", icon: Copy, enabled: (p) => !!p.numero,
      run: async (p) => {
        try { await navigator.clipboard.writeText(p.numero); toast.success(`${p.numero} copiado.`); }
        catch { toast.error("Nao foi possivel copiar."); }
      } },
    { key: "assinatura", label: "Assinatura", icon: PenLine, enabled: (p) => p.estado !== "assinada" && !!p.id,
      run: (p) => onOpenSign && onOpenSign(p) },
  ];
}

export default function ProposalDrawer({ proposal, open, onClose, accounts = [], onOpenPdf, onOpenSign, onOpenEditor, onAccepted }) {
  const [detail, setDetail] = useState(proposal || null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [aceitando, setAceitando] = useState(false);
  const [showAceitar, setShowAceitar] = useState(false);
  const [aceiteBody, setAceiteBody] = useState({ aceite_por: "", aceite_email: "", observacao: "" });
  const [shareLink, setShareLink] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [ttlDays, setTtlDays] = useState(30);
  const [sendEmail, setSendEmail] = useState(true);
  const [sendSms, setSendSms] = useState(false);
  const [toEmail, setToEmail] = useState("");
  const [toPhone, setToPhone] = useState("");
  const [coverLetters, setCoverLetters] = useState([]);
  const [coverLetterId, setCoverLetterId] = useState("");
  const [signatarios, setSignatarios] = useState([]);
  const [showSigModal, setShowSigModal] = useState(false);
  const [newSig, setNewSig] = useState({ nome: "", email: "", telefone: "" });
  const [revisions, setRevisions] = useState([]);
  const [showRevisions, setShowRevisions] = useState(false);
  const [diffPair, setDiffPair] = useState({ a: "", b: "" });
  const [showDiff, setShowDiff] = useState(false);

  useEffect(() => {
    if (!open || !proposal?.id) return;
    let cancel = false;
    setLoading(true);
    api.get(`/cpq/proposals/${proposal.id}`).then((r) => {
      if (cancel) return;
      setDetail(r.data); setItems(r.data.itens || []);
    }).catch(() => {
      if (!cancel) { setDetail(proposal); setItems(proposal.itens || []); }
    }).finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [open, proposal?.id]); // eslint-disable-line

  const p = detail || proposal || {};
  const enriched = { ...p, account_nome: accounts.find((a) => a.id === p.account_id)?.nome || p.account_nome || "-" };

  const canAccept = !!p.id && !["aceite", "assinada", "cancelada", "expirada"].includes(p.estado);

  const doAceitar = async () => {
    setAceitando(true);
    try {
      const { data } = await api.post(`/cpq/proposals/${p.id}/aceitar`, aceiteBody);
      const created = [
        data.order_id && "encomenda",
        data.contract_id && "contrato",
        data.invoice_id && "fatura",
        data.subscription_id && "subscricao",
      ].filter(Boolean);
      toast.success(`Proposta aceite. ${created.length ? "Criados: " + created.join(", ") : ""}`, { duration: 8000 });
      setShowAceitar(false);
      // Refrescar detail
      try { const r = await api.get(`/cpq/proposals/${p.id}`); setDetail(r.data); setItems(r.data.itens || []); } catch {}
      onAccepted && onAccepted(data);
    } catch (e) {
      const detail = e.response?.data?.detail || "Erro a aceitar proposta";
      toast.error(typeof detail === "string" ? detail : JSON.stringify(detail));
    } finally { setAceitando(false); }
  };
  const genShareLink = async () => {
    try {
      const payload = {
        ttl_days: parseInt(ttlDays) || 30,
        base_url: window.location.origin,
      };
      if (sendEmail) { payload.send_email = true; if (toEmail) payload.to_email = toEmail; }
      if (sendSms) { payload.send_sms = true; if (toPhone) payload.to_phone = toPhone; }
      if (coverLetterId) payload.cover_letter_id = coverLetterId;
      const { data } = await api.post(`/cpq/proposals/${p.id}/share-link`, payload);
      const fullUrl = data.full_url || `${window.location.origin}${data.url_path}`;
      setShareLink({ ...data, fullUrl });
      // Notificacoes com resultado do envio
      if (data.delivery?.email) {
        const em = data.delivery.email;
        if (em.estado === "enviado" || em.estado === "enviado_fake") toast.success(`Email enviado para ${em.para}`);
        else toast.warning(`Email: ${em.erro || em.estado}`);
      }
      if (data.delivery?.sms) {
        const sm = data.delivery.sms;
        if (sm.estado === "enviado" || sm.estado === "queued") toast.success(`SMS enviado para ${sm.para}`);
        else if (sm.estado === "sem_destino") toast.warning("SMS: sem telemovel do cliente.");
        else toast.error(`SMS: ${sm.erro || sm.estado}`);
      }
      try { await navigator.clipboard.writeText(fullUrl); toast.success("Link publico copiado."); }
      catch { toast.success("Link publico gerado."); }
    } catch (e) { toast.error(e.response?.data?.detail || "Erro a gerar link."); }
  };

  const loadRevisions = async () => {
    try {
      const { data } = await api.get(`/cpq/proposals/${p.id}/revisoes`);
      setRevisions(data || []);
    } catch { setRevisions([]); }
  };

  const restoreRevision = async (rid, versao) => {
    if (!window.confirm(`Restaurar a versao ${versao}? A versao actual sera guardada em historico antes de sobrescrever.`)) return;
    try {
      await api.post(`/cpq/proposals/${p.id}/revisoes/${rid}/restore`);
      toast.success(`Versao ${versao} restaurada.`);
      const r = await api.get(`/cpq/proposals/${p.id}`); setDetail(r.data); setItems(r.data.itens || []);
      loadRevisions();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro a restaurar."); }
  };

  useEffect(() => {
    if (open && p.id && showRevisions) loadRevisions();
  }, [open, p.id, showRevisions]); // eslint-disable-line

  // Load cover letters + signatarios ao abrir
  useEffect(() => {
    if (!open || !p.id) return;
    api.get("/cpq/cover-letters", { params: { ativos: true } }).then((r) => setCoverLetters(r.data || [])).catch(() => {});
    api.get(`/cpq/proposals/${p.id}/signatarios`).then((r) => setSignatarios(r.data || [])).catch(() => {});
  }, [open, p.id]); // eslint-disable-line

  const addSignatario = async () => {
    if (!newSig.nome?.trim()) return toast.error("Nome obrigatorio.");
    try {
      const { data } = await api.post(`/cpq/proposals/${p.id}/signatarios`, { ...newSig, canais: ["email"] });
      setSignatarios((prev) => [...prev, data]);
      setNewSig({ nome: "", email: "", telefone: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Erro."); }
  };
  const rmSignatario = async (sid) => {
    if (!window.confirm("Remover signatario?")) return;
    try {
      await api.delete(`/cpq/proposals/${p.id}/signatarios/${sid}`);
      setSignatarios((prev) => prev.filter((s) => s.id !== sid));
    } catch (e) { toast.error(e.response?.data?.detail || "Erro."); }
  };

  const exportDiffPdf = async () => {
    if (!diffPair.a || !diffPair.b) return toast.error("Escolha 2 versoes primeiro.");
    try {
      const { data } = await api.post(`/cpq/proposals/${p.id}/revisoes/diff-pdf`,
        { versao_a: diffPair.a, versao_b: diffPair.b },
        { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a"); a.href = url; a.download = `${p.numero || "proposta"}-diff.pdf`; a.click();
    } catch (e) { toast.error("Erro a exportar PDF do diff."); }
  };

  const placeholders = {
    id: p.id || "", numero: p.numero || "", titulo: p.titulo || "",
    cliente: enriched.account_nome || "", estado: p.estado || "",
    total: String(p.total || 0), subtotal: String(p.subtotal || 0),
    iva: String(p.total_iva ?? p.iva ?? 0), account_id: p.account_id || "",
  };

  const extraSections = [];
  if (items.length > 0) {
    extraSections.push({
      key: "itens", label: `Linhas (${items.length})`, defaultVisible: true,
      render: () => (
        <div className="space-y-1 max-h-64 overflow-y-auto">
          {items.map((it, i) => (
            <div key={i} className="flex items-center gap-2 bg-slate-50 rounded px-2 py-1.5">
              <div className="flex-1 min-w-0">
                <div className="text-[12px] font-medium text-navy truncate">{it.descricao || "-"}</div>
                <div className="text-[10px] text-slate-500">Qtd {it.quantidade || 0} · {eur(it.preco_unitario || 0)} · IVA {it.iva_pct || 0}%</div>
              </div>
              <div className="text-[12px] font-semibold text-navy">
                {eur(((it.quantidade || 0) * (it.preco_unitario || 0)) * (1 - (it.desconto_pct || 0) / 100))}
              </div>
            </div>
          ))}
        </div>
      ),
    });
  }
  if (p.estado === "aceite" || p.estado === "assinada") {
    extraSections.push({
      key: "cadeia", label: "Cadeia (encomenda -> contrato -> fatura)", defaultVisible: true,
      render: () => (
        <div className="space-y-1.5 text-[12px]">
          {p.aceite_em && <div>Aceite em <b>{dt(p.aceite_em)}</b>{p.aceite_por ? ` por ${p.aceite_por}` : ""}.</div>}
          {p.encomenda_id || p.order_id ? <div>Encomenda: <span className="font-mono">{p.encomenda_id || p.order_id}</span></div> : null}
          {p.contract_id && <div>Contrato: <span className="font-mono">{p.contract_id}</span></div>}
          {p.invoice_id && <div>Fatura: <span className="font-mono">{p.invoice_id}</span></div>}
          {!p.encomenda_id && !p.contract_id && !p.invoice_id && (
            <div className="text-slate-500 italic">Sem documentos ligados registados. Ver detalhes na ficha do cliente.</div>
          )}
        </div>
      ),
    });
  }
  if (p.id && p.estado !== "aceite" && p.estado !== "assinada" && p.estado !== "cancelada") {
    extraSections.push({
      key: "signatarios", label: `Co-signatarios (${signatarios.length})`, defaultVisible: false,
      render: () => (
        <div className="space-y-1.5" data-testid="drawer-signatarios">
          {signatarios.length === 0 && <div className="text-[11px] text-slate-500 italic">Sem co-signatarios. Se adicionar, todos precisam de assinar para a proposta ser fechada.</div>}
          {signatarios.map((s) => (
            <div key={s.id} className="bg-slate-50 rounded px-2 py-1.5 flex items-center justify-between text-[11px]">
              <div className="min-w-0">
                <div className="font-semibold text-navy">{s.nome}</div>
                <div className="text-slate-500 truncate">{s.email}{s.estado ? ` · ${s.estado}` : ""}</div>
              </div>
              {s.estado !== "assinou" && (
                <button onClick={() => rmSignatario(s.id)} data-testid={`rm-sig-${s.id}`} className="text-red-500 hover:bg-red-50 p-1 rounded"><Trash2 size={11} /></button>
              )}
            </div>
          ))}
          <div className="border-t border-tint pt-2 grid grid-cols-3 gap-1">
            <input type="text" value={newSig.nome} onChange={(e) => setNewSig({ ...newSig, nome: e.target.value })}
              placeholder="Nome" data-testid="new-sig-nome"
              className="px-2 py-1 border border-tint rounded text-[11px]" />
            <input type="email" value={newSig.email} onChange={(e) => setNewSig({ ...newSig, email: e.target.value })}
              placeholder="Email" data-testid="new-sig-email"
              className="px-2 py-1 border border-tint rounded text-[11px]" />
            <button onClick={addSignatario} data-testid="add-sig-btn"
              className="bg-navy text-white text-[11px] rounded px-2 py-1 hover:bg-navy/90">+ Adicionar</button>
          </div>
        </div>
      ),
    });
  }

  if (p.versao > 1 || revisions.length > 0) {
    extraSections.push({
      key: "revisoes", label: `Historico de versoes (v${p.versao || 1})`, defaultVisible: false,
      render: () => (
        <div className="space-y-1.5" data-testid="drawer-revisions">
          {!showRevisions ? (
            <button onClick={() => setShowRevisions(true)} data-testid="load-revisions-btn"
              className="text-[11px] text-blue hover:underline">Carregar historico...</button>
          ) : revisions.length === 0 ? (
            <div className="text-[11px] text-slate-500 italic">Sem revisoes registadas.</div>
          ) : (
            <>
              {revisions.length >= 2 && (
                <div className="flex items-center gap-1 text-[10px] bg-blue-50 rounded p-1.5">
                  <span className="font-semibold text-navy mr-1">Comparar:</span>
                  <select value={diffPair.a} onChange={(e) => setDiffPair({ ...diffPair, a: e.target.value })}
                    data-testid="diff-select-a" className="text-[10px] border border-tint rounded px-1 py-0.5 bg-white">
                    <option value="">A...</option>
                    <option value="__current__">v{p.versao || 1} (actual)</option>
                    {revisions.map((r) => <option key={r.id} value={r.id}>v{r.versao}</option>)}
                  </select>
                  <span className="text-slate-400">vs</span>
                  <select value={diffPair.b} onChange={(e) => setDiffPair({ ...diffPair, b: e.target.value })}
                    data-testid="diff-select-b" className="text-[10px] border border-tint rounded px-1 py-0.5 bg-white">
                    <option value="">B...</option>
                    <option value="__current__">v{p.versao || 1} (actual)</option>
                    {revisions.map((r) => <option key={r.id} value={r.id}>v{r.versao}</option>)}
                  </select>
                  <button onClick={() => setShowDiff(true)} disabled={!diffPair.a || !diffPair.b || diffPair.a === diffPair.b}
                    data-testid="diff-open-btn"
                    className="ml-1 px-2 py-0.5 bg-blue text-white rounded text-[10px] disabled:opacity-40">Ver diff</button>
                </div>
              )}
              {revisions.map((r) => (
                <div key={r.id} className="bg-slate-50 rounded px-2 py-1.5 flex items-center justify-between text-[11px]">
                  <div className="min-w-0">
                    <div className="font-semibold text-navy">v{r.versao} · {dt(r.alterado_em)}</div>
                    <div className="text-slate-500 truncate">Total: {eur(r.snapshot?.total || 0)} · {r.snapshot?.itens?.length || 0} linhas{r.motivo ? ` · ${r.motivo}` : ""}</div>
                  </div>
                  <button onClick={() => restoreRevision(r.id, r.versao)} data-testid={`restore-rev-${r.versao}`}
                    className="text-blue hover:bg-blue/10 rounded p-1" title="Restaurar esta versao">
                    <RotateCcw size={12} />
                  </button>
                </div>
              ))}
            </>
          )}
        </div>
      ),
    });
  }


  return (
    <>
    <ConfigurableDrawer
      open={open} onClose={onClose}
      overline="CPQ"
      title={p.titulo || (loading ? "A carregar..." : "Proposta")}
      testid="proposal-drawer"
      uiPrefKey="proposal_drawer" uiPrefVersion="v1"
      fieldDefs={FIELD_DEFS}
      actionDefs={makeActionDefs(onOpenPdf, onOpenSign)}
      defaultActions={DEFAULT_ACTIONS}
      defaultActionsVisible={DEFAULT_ACTIONS_VISIBLE}
      extraSections={extraSections}
      entity={enriched}
      entityLoading={loading && !detail}
      placeholders={placeholders}
      footer={
        <>
          <Btn variant="ghost" onClick={onClose} testid="proposal-drawer-close-btn">Fechar</Btn>
          {onOpenPdf && <Btn variant="ghost" onClick={() => onOpenPdf(p)} testid="proposal-drawer-pdf-btn"><FileText size={14} /> PDF</Btn>}
          {p.id && p.estado !== "aceite" && p.estado !== "assinada" && (
            <Btn variant="ghost" onClick={() => setShowShareModal(true)} testid="proposal-drawer-share-btn"><Link2 size={14} /> Link publico</Btn>
          )}
          {canAccept && <Btn variant="ghost" onClick={() => setShowAceitar(true)} testid="proposal-drawer-aceitar-btn"><CheckCircle2 size={14} /> Aceitar</Btn>}
          {onOpenEditor && p.id && <Btn onClick={() => onOpenEditor(p)} testid="proposal-drawer-editor-btn"><ExternalLink size={14} /> Abrir editor</Btn>}
        </>
      }
    />
    {showAceitar && (
      <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4" {...overlayCloseHandlers(() => !aceitando && setShowAceitar(false))}>
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-5 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="aceitar-modal">
          <div className="text-lg font-bold text-navy">Aceitar proposta {p.numero || ""}</div>
          <div className="text-sm text-slate-600">Ao aceitar, a proposta pode disparar automaticamente a criacao de encomenda, contrato, fatura e provisionamento (conforme configurado em Definicoes -&gt; Workflow CPQ).</div>
          <div className="space-y-2">
            <div>
              <label className="text-xs font-semibold text-navy">Aceite por (nome)</label>
              <input type="text" value={aceiteBody.aceite_por} onChange={(e) => setAceiteBody({ ...aceiteBody, aceite_por: e.target.value })}
                data-testid="aceite-por" className="w-full mt-1 px-3 py-2 border border-tint rounded text-sm" placeholder="Ex.: Joao Silva" />
            </div>
            <div>
              <label className="text-xs font-semibold text-navy">Email do aceitante</label>
              <input type="email" value={aceiteBody.aceite_email} onChange={(e) => setAceiteBody({ ...aceiteBody, aceite_email: e.target.value })}
                data-testid="aceite-email" className="w-full mt-1 px-3 py-2 border border-tint rounded text-sm" placeholder="joao@cliente.pt" />
            </div>
            <div>
              <label className="text-xs font-semibold text-navy">Observacao</label>
              <textarea value={aceiteBody.observacao} onChange={(e) => setAceiteBody({ ...aceiteBody, observacao: e.target.value })}
                data-testid="aceite-observacao" className="w-full mt-1 px-3 py-2 border border-tint rounded text-sm" rows={2} />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Btn variant="ghost" onClick={() => setShowAceitar(false)} disabled={aceitando}>Cancelar</Btn>
            <Btn onClick={doAceitar} disabled={aceitando} testid="aceite-confirmar">
              <CheckCircle2 size={14} /> {aceitando ? "A aceitar..." : "Confirmar aceitacao"}
            </Btn>
          </div>
        </div>
      </div>
    )}
    {showShareModal && (
      <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4" {...overlayCloseHandlers(() => setShowShareModal(false))}>
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-5 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="share-modal">
          <div className="text-lg font-bold text-navy flex items-center gap-2"><Link2 size={18} /> Link publico da proposta</div>
          <div className="text-xs text-slate-500">Gere um link seguro (HMAC) para o cliente ver e aceitar a proposta sem precisar de login Hub.</div>
          {!shareLink ? (
            <>
              <div>
                <label className="text-xs font-semibold text-navy">Validade do link (dias)</label>
                <input type="number" min="1" max="45" value={ttlDays} onChange={(e) => setTtlDays(e.target.value)}
                  data-testid="share-ttl" className="w-full mt-1 px-3 py-2 border border-tint rounded text-sm" />
              </div>
              <div className="border-t border-tint pt-3 space-y-2">
                <div className="text-xs font-semibold text-navy">Envio automatico (opcional)</div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)}
                    data-testid="share-send-email" />
                  <span>Enviar por email</span>
                </label>
                {sendEmail && (
                  <input type="email" value={toEmail} onChange={(e) => setToEmail(e.target.value)}
                    data-testid="share-to-email" placeholder="Deixar vazio usa email da conta"
                    className="w-full ml-6 px-3 py-1.5 border border-tint rounded text-xs" style={{width: 'calc(100% - 24px)'}} />
                )}
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={sendSms} onChange={(e) => setSendSms(e.target.checked)}
                    data-testid="share-send-sms" />
                  <span>Enviar por SMS</span>
                </label>
                {sendSms && (
                  <input type="tel" value={toPhone} onChange={(e) => setToPhone(e.target.value)}
                    data-testid="share-to-phone" placeholder="+3519XXXXXXXX (vazio usa contactos da conta)"
                    className="w-full ml-6 px-3 py-1.5 border border-tint rounded text-xs" style={{width: 'calc(100% - 24px)'}} />
                )}
                {sendEmail && coverLetters.length > 0 && (
                  <div>
                    <label className="text-xs font-semibold text-navy">Carta de apresentacao</label>
                    <select value={coverLetterId} onChange={(e) => setCoverLetterId(e.target.value)}
                      data-testid="share-cover-letter"
                      className="w-full mt-1 px-3 py-1.5 border border-tint rounded text-xs bg-white">
                      <option value="">Default (sem carta especifica)</option>
                      {coverLetters.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
                    </select>
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <Btn variant="ghost" onClick={() => setShowShareModal(false)}>Cancelar</Btn>
                <Btn onClick={genShareLink} testid="share-gen-btn"><Link2 size={14} /> Gerar link{(sendEmail || sendSms) ? " + enviar" : ""}</Btn>
              </div>
            </>
          ) : (
            <>
              <div className="bg-slate-50 border border-slate-200 rounded p-2 text-xs break-all font-mono" data-testid="share-url">
                {shareLink.fullUrl}
              </div>
              <div className="text-[11px] text-slate-500">Valido por {shareLink.ttl_days} dias.</div>
              <div className="flex justify-end gap-2">
                <Btn variant="ghost" onClick={() => { setShareLink(null); setShowShareModal(false); }}>Fechar</Btn>
                <Btn onClick={() => { navigator.clipboard.writeText(shareLink.fullUrl); toast.success("Copiado."); }} testid="share-copy-btn">
                  <Copy size={14} /> Copiar
                </Btn>
                <Btn onClick={() => window.open(shareLink.fullUrl, "_blank")} testid="share-open-btn">
                  <ExternalLink size={14} /> Abrir
                </Btn>
              </div>
            </>
          )}
        </div>
      </div>
    )}
    {showDiff && (() => {
      const getSnap = (key) => {
        if (key === "__current__") return p;
        const r = revisions.find((x) => x.id === key);
        return r?.snapshot || {};
      };
      const a = getSnap(diffPair.a);
      const b = getSnap(diffPair.b);
      const labelA = diffPair.a === "__current__" ? `v${p.versao || 1} (actual)` : `v${revisions.find((r) => r.id === diffPair.a)?.versao || "?"}`;
      const labelB = diffPair.b === "__current__" ? `v${p.versao || 1} (actual)` : `v${revisions.find((r) => r.id === diffPair.b)?.versao || "?"}`;
      const fields = [
        ["titulo", "Titulo"],
        ["total", "Total", (v) => eur(v || 0)],
        ["subtotal", "Subtotal", (v) => eur(v || 0)],
        ["desconto_pct_global", "Desconto global %", (v) => `${v || 0}%`],
        ["total_iva", "IVA", (v) => eur(v || 0)],
        ["mrr_estimado", "MRR", (v) => eur(v || 0)],
        ["condicoes_pagamento", "Condicoes"],
        ["metodo_pagamento", "Metodo pgto"],
        ["validade_dias", "Validade (dias)"],
        ["notas", "Notas"],
      ];
      return (
        <div className="fixed inset-0 z-[85] bg-black/50 flex items-center justify-center p-4" {...overlayCloseHandlers(() => setShowDiff(false))}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[85vh] overflow-y-auto p-5 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="diff-modal">
            <div className="text-lg font-bold text-navy flex items-center justify-between">
              <span>Comparar {labelA} vs {labelB}</span>
              <button onClick={() => setShowDiff(false)} className="text-slate-500 hover:text-navy text-2xl leading-none">&times;</button>
            </div>
            <div className="grid grid-cols-2 gap-2 sticky top-0 bg-white pt-1 pb-2 border-b border-slate-200">
              <div className="text-xs font-semibold text-blue-700 bg-blue-50 rounded p-2">{labelA}</div>
              <div className="text-xs font-semibold text-emerald-700 bg-emerald-50 rounded p-2">{labelB}</div>
            </div>
            <table className="w-full text-xs" data-testid="diff-fields">
              <tbody>
                {fields.map(([k, label, fmt]) => {
                  const va = a?.[k]; const vb = b?.[k];
                  const fa = fmt ? fmt(va) : (va === undefined || va === null || va === "" ? "-" : String(va));
                  const fb = fmt ? fmt(vb) : (vb === undefined || vb === null || vb === "" ? "-" : String(vb));
                  const diff = String(va ?? "") !== String(vb ?? "");
                  return (
                    <tr key={k} className={diff ? "bg-amber-50" : ""}>
                      <td className="py-1 font-semibold text-slate-600 w-32 pr-2">{label}</td>
                      <td className="py-1 pr-2 border-l border-slate-100 pl-2 text-blue-700">{fa}</td>
                      <td className="py-1 border-l border-slate-100 pl-2 text-emerald-700">{fb}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="text-xs font-bold text-navy pt-2">Linhas</div>
            <div className="grid grid-cols-2 gap-3 text-xs" data-testid="diff-items">
              {[a, b].map((snap, idx) => (
                <div key={idx} className="space-y-1">
                  {(snap.itens || []).map((it, i) => (
                    <div key={i} className={`rounded p-2 border-l-4 ${idx === 0 ? "border-blue-500 bg-blue-50" : "border-emerald-500 bg-emerald-50"}`}>
                      <div className="font-semibold text-navy truncate">{it.descricao}</div>
                      <div className="text-[11px] text-slate-500">Qtd {it.quantidade} · {eur(it.preco_unitario)} · IVA {it.iva_pct}% · {eur(it.total_linha || 0)}</div>
                    </div>
                  ))}
                  {(!snap.itens || snap.itens.length === 0) && <div className="italic text-slate-400 py-2">Sem linhas.</div>}
                </div>
              ))}
            </div>
            <div className="flex justify-end pt-2 gap-2">
              <Btn variant="ghost" onClick={exportDiffPdf} testid="diff-export-pdf-btn"><FileText size={14} /> Exportar PDF</Btn>
              <Btn onClick={() => setShowDiff(false)} testid="diff-close-btn">Fechar</Btn>
            </div>
          </div>
        </div>
      );
    })()}
    </>
  );
}
